import React, { useState } from 'react'
import { FaPenNib, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa'
import { Box, Button, Icon, Stack, Text, Badge, Select, SimplePopover, SimpleTable, Tooltip, ConfirmButton } from '@/components/lib'
import useStore from '@/store'
import api from '@/api'
import utils, { SIGNATURE_MEANINGS } from '@/utils/utils'
import useReauthentication from '@/hooks/useReauthentication'

// The electronic signature on a response: who signed it, when, and what
// they meant by it — or that it was signed and the data has changed since.
// Signing is a deliberate act behind the "confirm it's you" dialog
// (hooks/useReauthentication.jsx) and only for users holding
// trial.signResponses, which trial admins do not get by default.
//
// `signatures` is every signature on this response, newest first, as the
// API returns them alongside the responses; `onSigned` receives the new
// ones so the caller can add them to its list.

export function currentSignature (signatures) {
  return signatures?.find(s => s.status === 'valid') || signatures?.[0] || null
}

export default function ResponseSignature ({ response, signatures, onSigned }) {
  const { user, trial } = useStore()
  const { withReauth } = useReauthentication()
  const meanings = trial?.signatureMeanings?.length ? trial.signatureMeanings : SIGNATURE_MEANINGS
  const [meaning, setMeaning] = useState(meanings[0])
  const [signing, setSigning] = useState(false)
  const canSign = utils.canSignResponses(user, trial)
  const current = currentSignature(signatures)
  // Valid means nothing has superseded it; contentMatches (checked by the
  // API when read) means the response still holds what was signed. They
  // should agree — a mismatch is shown, never hidden.
  const valid = current?.status === 'valid'
  const mismatch = valid && current?.contentMatches === false

  function sign () {
    setSigning(true)
    withReauth('Signing this response', (token, resolve, reject) =>
      api.trials.signResponses(trial._id, { responses: [response._id], meaning }, token, resolve, reject)
    ).then(result => {
      setSigning(false)
      if (onSigned) onSigned(result.signatures || [])
      utils.toast('success', 'Response signed', `Signed as “${meaning}”.`)
    }).catch(err => {
      setSigning(false)
      if (!err?.cancelled) utils.toast('error', 'Unable to sign the response', err?.message)
    })
  }

  return (
    <Stack direction={['column', 'row']} alignItems={['stretch', 'center']} justifyContent='space-between' gap={2} p={2} rounded='md' borderWidth='1px' bg={valid && !mismatch ? 'green.50' : current ? 'orange.50' : 'bg.subtle'} _dark={{ bg: valid && !mismatch ? 'green.900' : current ? 'orange.900' : 'bg.subtle' }}>
      <Stack direction='row' alignItems='center' gap={2} flexWrap='wrap'>
        {current
          ? (
            <>
              <Icon as={valid && !mismatch ? FaCheckCircle : FaExclamationTriangle} color={valid && !mismatch ? 'green.500' : 'orange.500'} />
              <Text fontSize='xs'>
                {valid ? 'Signed' : 'Signature superseded'} by <strong>{current.signedByName}</strong> on {utils.formatDateTime(current.signedAt)} UTC (<em>{current.meaning}</em>)
                {!valid && ` (${current.supersededReason || 'the response was amended'} on ${utils.formatDateTime(current.supersededAt)}; it needs signing again)`}
                {mismatch && <strong> — the response no longer matches what was signed</strong>}
              </Text>
              {signatures.length > 1 &&
                <SimplePopover
                  lazy
                  contentProps={{ w: '640px', maxW: '95vw' }}
                  trigger={<Button size='2xs' variant='ghost'>{signatures.length} signatures</Button>}
                  header='Signatures on this response'
                  body={
                    <SimpleTable
                      size='sm'
                      headers={['Signed at', 'By', 'Meaning', 'Factor', 'Status']}
                      rows={signatures.map(s => [
                        utils.formatDateTime(s.signedAt),
                        s.signedByName,
                        s.meaning,
                        s.authMethod === 'passkey' ? 'Passkey' : 'Authenticator code',
                        s.status === 'valid'
                          ? <Badge key={s._id} colorPalette={s.contentMatches === false ? 'red' : 'green'}>{s.contentMatches === false ? 'content differs' : 'valid'}</Badge>
                          : <Tooltip key={s._id} label={`${s.supersededReason || 'Superseded'} on ${utils.formatDateTime(s.supersededAt)}`}><Badge colorPalette='orange'>superseded</Badge></Tooltip>
                      ])}
                    />
                  }
                />}
            </>
            )
          : <Text fontSize='sm' color='fg.muted'><Icon as={FaPenNib} mr={1} /> Not signed</Text>}
      </Stack>
      {canSign && !valid &&
        <Stack direction='row' alignItems='center' gap={2}>
          <Select size='xs' value={meaning} onChange={e => setMeaning(e.target.value)} variant='subtle' maxW='320px'>
            {meanings.map(m => <option key={m} value={m}>{m}</option>)}
          </Select>
          <ConfirmButton
            header={current ? 'Sign this response again?' : 'Sign this response?'}
            body={<Text>You will sign it as <em>“{meaning}”</em> and be asked to confirm it&apos;s you. The signature is recorded against this response as it stands now.</Text>}
            actionColor='primary'
            trigger={
              <Button size='xs' colorPalette='primary' loading={signing}>
                <Icon as={FaPenNib} mr={1} /> {current ? 'Sign again' : 'Sign'}
              </Button>
            }
            onConfirm={sign}
          />
        </Stack>}
      {canSign && valid &&
        <Box><Text fontSize='xs' color='fg.muted'>Amending the response will supersede this signature.</Text></Box>}
    </Stack>
  )
}
